import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ShoppingCart, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import useCanonicalUrl from '@/hooks/useCanonicalUrl';
import Layout from '@/components/layout/Layout';
import { Helmet } from 'react-helmet-async';
import JsonLd from '@/components/common/JsonLd';
import { getBreadcrumbSchema } from '@/lib/jsonld-schema';

interface Service {
    id: number;
    name: string;
    description: string;
    category_id: number;
    price: number;
}

export default function ServiceListingPage() {
    const [services, setServices] = useState<Service[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    
    // ============ PRIORITY 2: ADD CANONICAL URL ============
    useCanonicalUrl();

    const search = searchParams.get('search') || '';
    const category = searchParams.get('category') || '';

    useEffect(() => {
        loadServices();
    }, [search, category]);

    const loadServices = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (category) params.append('category', category);
            
            const queryString = params.toString() ? `?${params.toString()}` : '';
            const { getApiUrl } = await import('../config');
            const response = await fetch(getApiUrl(`/services${queryString}`));
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setServices(Array.isArray(data) ? data : data.data || []);
        } catch (error) {
            console.error('Load services error:', error);
            setError('Failed to load services. Please try again later.');
            setServices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = async (serviceId: number) => {
        if (!user) {
            alert('Please login to add items to cart');
            return;
        }

        try {
            const { getApiUrl } = await import('../config');
            const response = await fetch(getApiUrl('/cart/items'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    service_id: serviceId,
                    quantity: 1,
                    notes: ''
                })
            });

            if (response.ok) {
                alert('Added to cart!');
            } else {
                const data = await response.json();
                alert(data.message || 'Failed to add to cart');
            }
        } catch (error) {
            console.error('Add to cart error:', error);
            alert('Error adding to cart');
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>Services | VireWire</title>
                <meta name="description" content="Browse our complete range of professional property services including cleaning, maintenance, and logistics solutions." />
            </Helmet>
            {/* ============ PRIORITY 3: JSON-LD SCHEMA FOR ORGANIZATION ============ */}
            <JsonLd schema={getBreadcrumbSchema([
                { name: 'Home', url: window.location.origin },
                { name: 'Services', url: window.location.href }
            ])} />
            <div className="min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white shadow">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <h1 className="text-3xl font-bold text-gray-900">Browse Services</h1>
                        <p className="mt-2 text-gray-600">Find and book the services you need</p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Error Alert */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex gap-3">
                            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-red-800">Error Loading Services</h3>
                                <p className="text-red-700 text-sm">{error}</p>
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p className="text-gray-600">Loading services...</p>
                            </div>
                        </div>
                    ) : services.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-lg shadow">
                            <p className="text-gray-600 text-lg mb-4">No services found</p>
                            <Link
                                to="/services"
                                className="inline-block px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Back to Services
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {services.map((service) => (
                                <div
                                    key={service.id}
                                    className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden"
                                >
                                    <div className="p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                            {service.name}
                                        </h3>
                                        <p className="text-gray-600 text-sm mb-4">
                                            {service.description || 'Professional service'}
                                        </p>

                                        <div className="flex items-baseline justify-between mb-4">
                                            <div>
                                                <span className="text-2xl font-bold text-gray-900">
                                                    ${service.price?.toFixed(2) || '0.00'}
                                                </span>
                                                <span className="text-gray-600 text-sm ml-1">per service</span>
                                            </div>
                                        </div>

                                        <div className="flex gap-2">
                                            <Link
                                                to={`/services/${service.id}`}
                                                className="flex-1 px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 transition-colors text-center text-sm font-medium"
                                            >
                                                View Details
                                            </Link>
                                            <button
                                                onClick={() => handleAddToCart(service.id)}
                                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                                            >
                                                <ShoppingCart size={16} />
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
